import { getOnlyDate } from '~/astra'
import { useFlag } from '~/hooks/useFlag'
import { Location, useLocationScheduleSpecialities, useOrderedLocations, usePractitioners, usePractitionerScheduleLocations } from '~/storm'

import { DatePractitionerCell } from '../../../shared'

type LocationGroup = {
    available: Location[]
    unavailable: Location[]
    maxCount: number
}

type LocationGroups = {
    selectedSpecialties: LocationGroup
    otherSpecialties: LocationGroup
}

export const useLocationsForPractitionerScheduleModal = (selectedCells: DatePractitionerCell[]) => {
    const isSpecialtiesSelectionEnabled = useFlag('use_specialties_selection')

    const locations = useOrderedLocations()

    const practitionersById = usePractitioners({
        where: { id: { in: [...new Set(selectedCells.map(({ practitionerId }) => practitionerId))] } },
        include: { departments: { locations: true } },
        keyBy: 'id',
    })

    const selectedDates = [...new Set(selectedCells.map(({ date }) => getOnlyDate(date)))]
    const locationScheduleSpecialities = useLocationScheduleSpecialities({ where: { scheduled_date: { in: selectedDates } } })
    const practitionerScheduleLocations = usePractitionerScheduleLocations({ where: { scheduled_date: { in: selectedDates } } })

    const locationGroups: LocationGroups = {
        selectedSpecialties: { available: [], unavailable: [], maxCount: 0 },
        otherSpecialties: { available: [], unavailable: [], maxCount: 0 },
    }

    for (const location of locations) {
        const matchesEveryCell =
            selectedCells.length > 0 &&
            selectedCells.every(({ date, practitionerId }) => {
                const practitioner = practitionersById.get(practitionerId)

                if (!practitioner) return false

                // Check if the location is allocated to the practitioner's speciality on this date
                if (isSpecialtiesSelectionEnabled) {
                    return locationScheduleSpecialities.some(
                        locationScheduleSpeciality =>
                            locationScheduleSpeciality.location_id === location.id &&
                            locationScheduleSpeciality.speciality_uuid === practitioner.speciality_uuid &&
                            locationScheduleSpeciality.scheduled_date === getOnlyDate(date)
                    )
                }

                // Check if the location is part of at least one of the practitioner's departments
                return practitioner.departments.some(department => department.locations.some(departmentLocation => departmentLocation.id === location.id))
            })

        // Check if the selected cells have assigned practitioners for this location
        const isUnavailable = selectedCells.some(({ date }) =>
            practitionerScheduleLocations.some(schedule => schedule.location_id === location.id && schedule.scheduled_date === getOnlyDate(date))
        )

        // Push the location to the right group and subgroup
        const specialtyGroup = matchesEveryCell ? locationGroups.selectedSpecialties : locationGroups.otherSpecialties
        specialtyGroup[isUnavailable ? 'unavailable' : 'available'].push(location)
    }

    for (const group of Object.values(locationGroups)) {
        group.maxCount = group.available.length + group.unavailable.length
    }

    return { locationGroups, isSpecialtiesSelectionEnabled }
}
